import { useState, useEffect, useCallback } from 'react';
import {
  User,
  Calendar,
  Clock,
  Sun,
  Briefcase,
  ArrowLeft,
  ArrowRight,
  Download,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Save,
  Edit3,
  X,
  Check,
  AlertTriangle,
  Users,
  Plus,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import { useAPIStorage } from '../../hooks/useAPIStorage';
import { calculateResults, calculatePayrollMonthlyData, formatNumber, formatCurrency, findSegmentWageType, recalcFutureVacationHours } from '../../utils/calculations';
import { getPayrollPeriodsForYear } from '../../utils/payrollPeriod';
import {
  getVacationFinalizationState,
  getVacationFinalizationBadge,
  type VacationFinalizationBadge,
} from '../../utils/vacationFinalization';
import { DEFAULT_SETTINGS, VALIDATION } from '../../types';
import { FEDERAL_STATE_NAMES, type FederalState } from '../../data/germanHolidays';
import type { CompanySettings, DayEntry, VacationCarryOver, Department, UserEntry, EmploymentType, WageType, ManagerAssignment, VacationAdjustment, VacationAdjustmentCategory, EmploymentHistoryRecord, CreateHistoryRequest, SettingsSegment } from '../../types';
import { EMPLOYMENT_TYPES, EMPLOYMENT_TYPE_LABELS, WAGE_TYPES, WAGE_TYPE_LABELS, VACATION_ADJUSTMENT_CATEGORY_LABELS } from '../../types';
import { EmploymentTimeline } from './EmploymentTimeline';
import { EmploymentChangeDialog } from './EmploymentChangeDialog';
import { Table, TableHeader, TableBody, TableFooter, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
// Diese Datei hat weiter unten eine eigene, ältere `Badge` mit nur zwei
// Varianten. Für die Festsetzungs-Spalte wird die Komponente aus dem
// Design-System gebraucht (success/info, plus title für den Hinweistext).
import { Badge as StatusBadge } from '@/components/ui/badge';

/**
 * Anzeige der §11-Festsetzung pro Abrechnungszeitraum.
 *
 * Zweck ist Sichtbarkeit: aus dieser Tabelle werden die Stunden ans Lohnbüro
 * abgeschrieben. Ist ein Zeitraum nicht festgesetzt, wird die Zahl bei jedem
 * Aufruf neu aus dem 13-Wochen-Fenster berechnet und kann sich nach der Meldung
 * noch verschieben. Ohne diese Spalte wäre ein vergessenes Festsetzen unsichtbar
 * — und unsichtbar ist die Eigenschaft, die den Meldefehler ein halbes Jahr
 * überleben ließ.
 */
const FINALIZATION_LABELS: Record<
  VacationFinalizationBadge,
  { label: string; variant: 'success' | 'warning' | 'info' | 'purple'; title: string } | null
> = {
  none: null,
  open: {
    label: 'offen',
    variant: 'warning',
    title:
      'Urlaubsstunden sind noch nicht festgesetzt. Der Wert wird bei jedem Aufruf neu '
      + 'berechnet und kann sich ändern, wenn Arbeitsstunden nachgetragen werden. '
      + 'Festsetzen im Lohn-Export.',
  },
  withoutBasis: {
    label: 'ohne Grundlage',
    variant: 'purple',
    title:
      'Einzelne Urlaubstage gehen mit 0 Std. in die Meldung ein, weil im 91-Tage-Fenster '
      + 'vor dem Urlaub kein Arbeitstag liegt. Das kann korrekt sein (Urlaub vor dem '
      + 'ersten Arbeitstag), sonst fehlen Arbeitsstunden. Der Festsetzen-Button lässt '
      + 'diese Tage bewusst in Ruhe, damit die 0 nicht dauerhaft festgeschrieben wird.',
  },
  provisional: {
    label: 'vorläufig',
    variant: 'info',
    title:
      'Urlaub liegt in der Zukunft. Die Stunden bleiben absichtlich dynamisch, bis der '
      + 'Zeitraum vorbei ist.',
  },
  finalized: {
    label: 'festgesetzt',
    variant: 'success',
    title: 'Urlaubsstunden sind festgeschrieben und ändern sich nicht mehr automatisch.',
  },
};

const DEPARTMENTS: Department[] = [
  'SEO',
  'SEA',
  'Social',
  'Content',
];

const DEPARTMENT_NAMES: Record<Department, string> = {
  SEO: 'SEO',
  SEA: 'SEA',
  Social: 'Social Media',
  Content: 'Content',
};

interface Props {
  user: UserEntry;
  onBack: () => void;
  onUserUpdated?: (updated: UserEntry) => void;
  /** 'hr' = voller Zugriff, 'manager' = read-only ohne Lohndaten */
  viewMode?: 'hr' | 'manager';
  /** Optional tab bar to render in header (for Details/Kalender tab switching) */
  tabBar?: React.ReactNode;
}

interface EmployeeData {
  settings: CompanySettings | null;
  entries: Record<string, DayEntry> | null;
  carryOver: VacationCarryOver | null;
  previousYearEntries: Record<string, DayEntry> | null;
}

/** Alle editierbaren Felder in einem Objekt gebündelt */
interface EditableData {
  // Stammdaten (PATCH /users/:id)
  personnelNumber: string;
  // E-Mail (PATCH /users/:id/email — eigener Endpoint, nur solange nicht Azure-verknüpft)
  email: string;
  firstName: string;
  lastName: string;
  employmentStart: string;
  employmentEnd: string;
  employmentType: EmploymentType | '';
  wageType: WageType | '';
  monthlySalary: string;
  contractualHours: string;
  rv: boolean;
  hansefit: boolean;
  excludeFromExport: boolean;
  // Betriebsdaten (PUT /settings/:id)
  federalState: FederalState;
  department: Department | '';
  vacationDaysFullTime: number;
  workDaysPerWeekFullTime: 5 | 6;
  hoursPerDayFullTime: number;
  hourlyWage: number;
}

export function EmployeeDetail({ user, onBack, onUserUpdated, viewMode = 'hr', tabBar }: Props) {
  const isManagerView = viewMode === 'manager';
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState<EmployeeData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Einheitlicher Edit-Modus
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<EditableData | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Lokale Kopie der User-Stammdaten (für Read-Only-Anzeige, aktualisiert nach Save)
  const [localUser, setLocalUser] = useState(user);

  // Manager-Zuordnung
  const [managerAssignments, setManagerAssignments] = useState<ManagerAssignment[]>([]);
  const [allUsers, setAllUsers] = useState<UserEntry[]>([]);
  const [selectedManagerIds, setSelectedManagerIds] = useState<string[]>([]);
  const [isSavingManagers, setIsSavingManagers] = useState(false);
  const [managerSearchTerm, setManagerSearchTerm] = useState('');
  const [showManagerDropdown, setShowManagerDropdown] = useState(false);

  // Urlaubsübertrag
  const [showCarryOverDialog, setShowCarryOverDialog] = useState(false);
  const [carryOverAmount, setCarryOverAmount] = useState(0);
  const [isSavingCarryOver, setIsSavingCarryOver] = useState(false);
  const [carryOverSuccess, setCarryOverSuccess] = useState<string | null>(null);
  const [isRecalculating, setIsRecalculating] = useState(false);

  // Urlaubsanpassungen
  const [adjustments, setAdjustments] = useState<VacationAdjustment[]>([]);
  const [showAdjustmentDialog, setShowAdjustmentDialog] = useState(false);
  const [adjCategory, setAdjCategory] = useState<VacationAdjustmentCategory>('SPECIAL_LEAVE');
  const [adjDays, setAdjDays] = useState('');
  const [adjSign, setAdjSign] = useState<'add' | 'subtract'>('add');
  const [adjReason, setAdjReason] = useState('');
  const [isSavingAdjustment, setIsSavingAdjustment] = useState(false);
  const [adjustmentSuccess, setAdjustmentSuccess] = useState<string | null>(null);
  const [deletingAdjustmentId, setDeletingAdjustmentId] = useState<string | null>(null);

  // Genommene Urlaubstage aufklappbar
  const [showVacationDays, setShowVacationDays] = useState(false);

  // Beschäftigungshistorie
  const [historyRecords, setHistoryRecords] = useState<EmploymentHistoryRecord[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyDialogMode, setHistoryDialogMode] = useState<'create' | 'edit'>('create');
  const [historyEditRecord, setHistoryEditRecord] = useState<EmploymentHistoryRecord | undefined>(undefined);
  const [showDeleteHistoryConfirm, setShowDeleteHistoryConfirm] = useState<string | null>(null);

  // Settings-Segmente für §5 BUrlG Teilurlaub-Berechnung
  const [settingsSegments, setSettingsSegments] = useState<SettingsSegment[]>([]);

  const { getUserSettings, getUserEntries, getUserCarryOver, saveUserSettings, saveUserCarryOver, autoCalculateCarryover, updateUser, updateUserEmail, getManagerAssignments, updateManagerAssignments, getAllUsers, getVacationAdjustments, addVacationAdjustment, deleteVacationAdjustment, getEmploymentHistory, createHistoryRecord, updateHistoryRecord, deleteHistoryRecord, getUserSettingsSegments } = useAPIStorage();

  const loadUserData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setSaveError(null);

    try {
      // Lade Settings, Entries (aktuell + Vorjahr), CarryOver und SettingsSegments parallel
      const [settings, entries, carryOver, previousYearEntries, segments] = await Promise.all([
        getUserSettings(user.id),
        getUserEntries(user.id, year),
        getUserCarryOver(user.id, year),
        getUserEntries(user.id, year - 1),
        getUserSettingsSegments(user.id, year).catch(() => []),
      ]);
      setData({
        settings,
        entries,
        carryOver,
        previousYearEntries: previousYearEntries || null,
      });
      setSettingsSegments(segments ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler beim Laden der Daten');
    } finally {
      setIsLoading(false);
    }
  }, [user.id, year, getUserSettings, getUserEntries, getUserCarryOver, getUserSettingsSegments]);

  useEffect(() => {
    void loadUserData();
  }, [loadUserData]);

  // localUser aktualisieren wenn user-Prop sich ändert
  useEffect(() => {
    setLocalUser(user);
  }, [user]);

  // Manager-Zuordnungen + alle User laden (nur HR-Ansicht)
  useEffect(() => {
    if (isManagerView) return; // Manager brauchen keine Zuordnungsdaten
    const loadManagerData = async () => {
      const [assignments, users] = await Promise.all([
        getManagerAssignments(user.id),
        getAllUsers(),
      ]);
      setManagerAssignments(assignments);
      setSelectedManagerIds(assignments.map((a) => a.managerId));
      setAllUsers(users);
    };
    void loadManagerData();
  }, [user.id, getManagerAssignments, getAllUsers, isManagerView]);

  // Urlaubsanpassungen laden (nur HR-Ansicht)
  useEffect(() => {
    if (isManagerView) return;
    const loadAdjustments = async () => {
      const adj = await getVacationAdjustments(user.id, year);
      setAdjustments(adj);
    };
    void loadAdjustments();
  }, [user.id, year, getVacationAdjustments, isManagerView]);

  // Beschäftigungshistorie laden (nur HR-Ansicht)
  const loadHistory = useCallback(async () => {
    if (isManagerView) return;
    setIsHistoryLoading(true);
    setHistoryError(null);
    try {
      const records = await getEmploymentHistory(user.id);
      setHistoryRecords(records);
    } catch (err) {
      console.error('Fehler beim Laden der Beschäftigungshistorie:', err);
      setHistoryError('Historie konnte nicht geladen werden');
    } finally {
      setIsHistoryLoading(false);
    }
  }, [user.id, getEmploymentHistory, isManagerView]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const handleAddHistory = () => {
    setHistoryDialogMode('create');
    setHistoryEditRecord(undefined);
    setShowHistoryDialog(true);
  };

  const handleEditHistory = (record: EmploymentHistoryRecord) => {
    setHistoryDialogMode('edit');
    setHistoryEditRecord(record);
    setShowHistoryDialog(true);
  };

  const handleSaveHistory = async (data: CreateHistoryRequest) => {
    if (historyDialogMode === 'edit' && historyEditRecord) {
      await updateHistoryRecord(user.id, historyEditRecord.id, data);
    } else {
      await createHistoryRecord(user.id, data);
    }
    setShowHistoryDialog(false);
    await loadHistory();
    // User-Daten könnten sich geändert haben (department, hourlyWage etc.)
    await loadUserData();
  };

  const handleDeleteHistory = async (recordId: string) => {
    await deleteHistoryRecord(user.id, recordId);
    setShowDeleteHistoryConfirm(null);
    await loadHistory();
    await loadUserData();
  };

  // Aktuell gültiger Record (für Vorausfüllung im Dialog)
  const currentHistoryRecord = historyRecords.find((r) => {
    const today = new Date().toISOString().slice(0, 10);
    return r.validFrom <= today && (!r.validTo || r.validTo >= today);
  }) ?? null;

  const settings = data?.settings || DEFAULT_SETTINGS;
  const entries = data?.entries || {};
  const carryOver = data?.carryOver?.days ?? 0;
  const previousYearEntries = data?.previousYearEntries || {};
  const adjustmentSum = adjustments.reduce((sum, a) => sum + a.days, 0);

  const results = calculateResults(
    entries, settings, year, new Date(), carryOver, previousYearEntries, adjustmentSum,
    user.employmentStart, user.employmentEnd,
    settingsSegments.length > 0 ? settingsSegments : undefined
  );
  const monthlyData = calculatePayrollMonthlyData(entries, year, previousYearEntries);

  // Festsetzungs-Status je Abrechnungszeitraum. Dieselbe Neuberechnung, die
  // calculatePayrollMonthlyData intern anwendet — hier wird zusätzlich die Map
  // gebraucht, um vorläufige von festsetzbaren Tagen unterscheiden zu können.
  const vacationRecalc = recalcFutureVacationHours(entries, previousYearEntries);
  const finalizationByMonth = new Map(
    getPayrollPeriodsForYear(year).map((p) => [
      p.month,
      getVacationFinalizationBadge(getVacationFinalizationState(entries, vacationRecalc, p)),
    ])
  );

  // --- Edit-Modus-Logik ---

  const handleStartEdit = () => {
    setEditData({
      personnelNumber: localUser.personnelNumber ?? '',
      email: localUser.email ?? '',
      firstName: localUser.firstName ?? '',
      lastName: localUser.lastName ?? '',
      employmentStart: localUser.employmentStart ?? '',
      employmentEnd: localUser.employmentEnd ?? '',
      employmentType: localUser.employmentType ?? '',
      wageType: localUser.wageType ?? '',
      monthlySalary: localUser.monthlySalary != null ? String(localUser.monthlySalary) : '',
      contractualHours: localUser.contractualHours != null ? String(localUser.contractualHours) : '',
      rv: localUser.rv ?? false,
      hansefit: localUser.hansefit ?? false,
      excludeFromExport: localUser.excludeFromExport ?? false,
      federalState: settings.federalState,
      department: settings.department || '',
      vacationDaysFullTime: settings.vacationDaysFullTime,
      workDaysPerWeekFullTime: settings.workDaysPerWeekFullTime,
      hoursPerDayFullTime: settings.hoursPerDayFullTime,
      hourlyWage: settings.hourlyWage,
    });
    setIsEditing(true);
    setSaveError(null);
  };

  const handleCancelEdit = () => {
    setEditData(null);
    setIsEditing(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!editData) return;

    setIsSaving(true);
    setSaveError(null);

    // E-Mail-Änderung vorbereiten: nur wenn nicht Azure-verknüpft und tatsächlich geändert.
    const trimmedEmail = editData.email.trim();
    const emailChanged = !localUser.azureLinked && trimmedEmail !== localUser.email;
    if (emailChanged && !trimmedEmail.toLowerCase().endsWith('@suchhelden.de')) {
      setSaveError('E-Mail-Adresse muss auf @suchhelden.de enden.');
      setIsSaving(false);
      return;
    }

    try {
      // 0. E-Mail zuerst (eigener Endpoint, kann mit 409 fehlschlagen → dann bricht der Save sauber ab)
      if (emailChanged) {
        await updateUserEmail(user.id, trimmedEmail);
      }

      // 1. Stammdaten speichern (PATCH /users/:id)
      await updateUser(user.id, {
        personnelNumber: editData.personnelNumber.trim() || null,
        firstName: editData.firstName,
        lastName: editData.lastName,
        employmentStart: editData.employmentStart || null,
        employmentEnd: editData.employmentEnd || null,
        employmentType: editData.employmentType || null,
        excludeFromExport: editData.excludeFromExport,
        rv: editData.rv,
        hansefit: editData.hansefit,
        contractualHours: editData.contractualHours !== '' ? Number(editData.contractualHours) : null,
        wageType: editData.wageType || null,
        monthlySalary: editData.monthlySalary !== '' ? Number(editData.monthlySalary) : null,
      });

      // 2. Betriebsdaten speichern (PUT /settings/:id)
      const newSettings: CompanySettings = {
        ...settings,
        federalState: editData.federalState,
        department: editData.department || undefined,
        vacationDaysFullTime: editData.vacationDaysFullTime,
        workDaysPerWeekFullTime: editData.workDaysPerWeekFullTime,
        hoursPerDayFullTime: editData.hoursPerDayFullTime,
        hourlyWage: editData.hourlyWage,
      };
      await saveUserSettings(user.id, newSettings);

      // Lokale Daten aktualisieren
      const updatedUser = {
        ...localUser,
        personnelNumber: editData.personnelNumber.trim() || null,
        email: emailChanged ? trimmedEmail : localUser.email,
        firstName: editData.firstName,
        lastName: editData.lastName,
        employmentStart: editData.employmentStart || null,
        employmentEnd: editData.employmentEnd || null,
        employmentType: editData.employmentType || null,
        excludeFromExport: editData.excludeFromExport,
        rv: editData.rv,
        hansefit: editData.hansefit,
        contractualHours: editData.contractualHours !== '' ? Number(editData.contractualHours) : null,
        wageType: editData.wageType || null,
        monthlySalary: editData.monthlySalary !== '' ? Number(editData.monthlySalary) : null,
      };
      setLocalUser(updatedUser);
      setData((prev) => prev ? { ...prev, settings: newSettings } : null);

      if (onUserUpdated) {
        onUserUpdated(updatedUser);
      }

      setIsEditing(false);
      setEditData(null);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditChange = <K extends keyof EditableData>(key: K, value: EditableData[K]) => {
    if (!editData) return;
    setEditData({ ...editData, [key]: value });
  };

  const handleSettingNumberChange = (key: 'vacationDaysFullTime' | 'hoursPerDayFullTime' | 'hourlyWage', value: string) => {
    if (!editData) return;
    setEditData({ ...editData, [key]: parseFloat(value) || 0 });
  };

  // --- Manager-Zuordnung ---

  const handleSaveManagers = async () => {
    setIsSavingManagers(true);
    setSaveError(null);
    try {
      await updateManagerAssignments(user.id, selectedManagerIds);
      const updated = await getManagerAssignments(user.id);
      setManagerAssignments(updated);
      setSelectedManagerIds(updated.map((a) => a.managerId));
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Fehler beim Speichern der Manager');
    } finally {
      setIsSavingManagers(false);
    }
  };

  const handleAddManager = (managerId: string) => {
    if (!selectedManagerIds.includes(managerId)) {
      setSelectedManagerIds([...selectedManagerIds, managerId]);
    }
    setManagerSearchTerm('');
    setShowManagerDropdown(false);
  };

  const handleRemoveManager = (managerId: string) => {
    setSelectedManagerIds(selectedManagerIds.filter((id) => id !== managerId));
  };

  // Prüfe ob sich Manager-Zuordnungen geändert haben
  const managersChanged = JSON.stringify([...selectedManagerIds].sort()) !==
    JSON.stringify([...managerAssignments.map((a) => a.managerId)].sort());

  // --- Urlaubsübertrag ---

  const handleOpenCarryOver = () => {
    const available = Math.max(0, results.remainingVacation);
    setCarryOverAmount(available);
    setShowCarryOverDialog(true);
    setCarryOverSuccess(null);
  };

  const handleCarryOver = async () => {
    if (carryOverAmount <= 0) return;
    setIsSavingCarryOver(true);
    setSaveError(null);
    try {
      await saveUserCarryOver(user.id, year + 1, {
        days: carryOverAmount,
        fromYear: year,
        expirationDate: `${year + 1}-03-31`,
        expired: false,
      });
      setShowCarryOverDialog(false);
      setCarryOverSuccess(
        `${carryOverAmount} Urlaubstage von ${year} wurden nach ${year + 1} für ${localUser.firstName && localUser.lastName ? `${localUser.firstName} ${localUser.lastName}` : localUser.name} übertragen.`
      );
      await loadUserData();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Fehler beim Übertragen');
    } finally {
      setIsSavingCarryOver(false);
    }
  };

  const handleRecalculateCarryover = async () => {
    if (!confirm(`Urlaubsübertrag für ${year} von ${localUser.firstName || ''} ${localUser.lastName || localUser.name} neu berechnen? Der bestehende Wert wird überschrieben.`)) return;
    setIsRecalculating(true);
    try {
      const result = await autoCalculateCarryover(year, user.id, true);
      setCarryOverSuccess(`Übertrag für ${year} neu berechnet: ${result.days} Tage`);
      await loadUserData();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Fehler bei der Neuberechnung');
    } finally {
      setIsRecalculating(false);
    }
  };

  // --- Urlaubsanpassungen ---

  const handleOpenAdjustmentDialog = () => {
    setAdjCategory('SPECIAL_LEAVE');
    setAdjDays('');
    setAdjSign('add');
    setAdjReason('');
    setShowAdjustmentDialog(true);
    setAdjustmentSuccess(null);
    setSaveError(null);
  };

  const handleSaveAdjustment = async () => {
    const daysNum = parseFloat(adjDays);
    if (!daysNum || daysNum <= 0) return;
    if (adjReason.trim().length < 3) return;

    const effectiveDays = adjSign === 'subtract' ? -daysNum : daysNum;

    setIsSavingAdjustment(true);
    setSaveError(null);
    try {
      await addVacationAdjustment(user.id, year, {
        days: effectiveDays,
        category: adjCategory,
        reason: adjReason.trim(),
      });
      setShowAdjustmentDialog(false);
      setAdjustmentSuccess(
        `${Math.abs(effectiveDays)} Urlaubstag${Math.abs(effectiveDays) !== 1 ? 'e' : ''} ${effectiveDays > 0 ? 'hinzugefügt' : 'abgezogen'} für ${localUser.firstName && localUser.lastName ? `${localUser.firstName} ${localUser.lastName}` : localUser.name}.`
      );
      const adj = await getVacationAdjustments(user.id, year);
      setAdjustments(adj);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Fehler beim Speichern der Anpassung');
    } finally {
      setIsSavingAdjustment(false);
    }
  };

  const handleDeleteAdjustment = async (adjustmentId: string) => {
    setDeletingAdjustmentId(adjustmentId);
    setSaveError(null);
    try {
      await deleteVacationAdjustment(user.id, adjustmentId);
      const adj = await getVacationAdjustments(user.id, year);
      setAdjustments(adj);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Fehler beim Löschen der Anpassung');
    } finally {
      setDeletingAdjustmentId(null);
    }
  };

  // --- CSV-Export ---

  const handleExportCSV = () => {
    const headers = ['Monat', 'Arbeitsstunden', 'Arbeitstage', 'Feiertage', 'Urlaubstage'];
    const rows = monthlyData.map((m) => [
      m.monthName,
      formatNumber(m.totalHours),
      m.workDays.toString(),
      m.holidays.toString(),
      m.vacationDays.toString(),
    ]);

    const totals = [
      'Gesamt',
      formatNumber(results.totalHours),
      results.workDays.toString(),
      results.holidays.toString(),
      results.vacationDaysTaken.toString(),
    ];

    const csv = [headers, ...rows, totals]
      .map((row) => row.join(';'))
      .join('\n');

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const displayName = localUser.firstName && localUser.lastName ? `${localUser.lastName}_${localUser.firstName}` : localUser.name;
    a.download = `${displayName.replace(/\s+/g, '_')}_${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- Render ---

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-center gap-3 text-gray-500 dark:text-gray-400">
            <div className="w-5 h-5 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin" />
            <span>Lade Mitarbeiterdaten...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <Button
              variant="ghost"
              onClick={onBack}
            >
              Zurück zur Liste
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const displayName = localUser.firstName && localUser.lastName ? `${localUser.firstName} ${localUser.lastName}` : localUser.name;

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={onBack}
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">{displayName}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{localUser.email}</p>
                </div>
              </div>
              {tabBar}
            </div>

            {/* Jahr-Auswahl */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setYear((y) => y - 1)}
              >
                <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </Button>
              <span className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100 min-w-[80px] text-center">
                {year}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setYear((y) => y + 1)}
                disabled={year >= new Date().getFullYear()}
              >
                <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════════════
          Block 1: Personalstammdaten (HR-editierbar)
          ═══════════════════════════════════════════════ */}
      <Card>
        <CardContent className="p-5">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-gray-900 dark:text-gray-50 flex items-center gap-2">
            <User className="w-5 h-5" />
            Personalstammdaten
          </h3>
          {!isManagerView && !isEditing && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleStartEdit}
            >
              <Edit3 className="w-4 h-4" />
              Bearbeiten
            </Button>
          )}
          {!isManagerView && isEditing && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                <X className="w-4 h-4" />
                Abbrechen
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Speichern...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Speichern
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {saveError && (
          <div className="mb-4 p-3 border border-red-300/60 bg-red-50 dark:border-red-500/20 dark:bg-red-900/10 rounded-lg text-sm text-red-700 dark:text-red-300">
            {saveError}
          </div>
        )}

        {/* Persönliche Daten */}
        <SectionHeading>Persönliche Daten</SectionHeading>
        {isEditing && editData ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            {!isManagerView && (
              <EditField label="E-Mail" id="ed-email">
                {localUser.azureLinked ? (
                  <>
                    <Input
                      type="email"
                      id="ed-email"
                      value={editData.email}
                      disabled
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Mit Azure verknüpft — die E-Mail ist an die Anmeldung gebunden und kann nicht mehr geändert werden.
                    </p>
                  </>
                ) : (
                  <>
                    <Input
                      type="email"
                      id="ed-email"
                      maxLength={255}
                      value={editData.email}
                      onChange={(e) => handleEditChange('email', e.target.value)}
                      placeholder="vorname.nachname@suchhelden.de"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Muss auf @suchhelden.de enden und der Azure-Anmeldung entsprechen. Nur änderbar, solange sich der Mitarbeiter noch nie angemeldet hat.
                    </p>
                  </>
                )}
              </EditField>
            )}
            {!isManagerView && (
              <EditField label="Personalnummer" id="ed-personnelNumber">
                <Input
                  type="text"
                  id="ed-personnelNumber"
                  maxLength={50}
                  value={editData.personnelNumber}
                  onChange={(e) => handleEditChange('personnelNumber', e.target.value)}
                  placeholder="z.B. 12345"
                />
              </EditField>
            )}
            <EditField label="Vorname" id="ed-firstName">
              <Input
                type="text"
                id="ed-firstName"
                value={editData.firstName}
                onChange={(e) => handleEditChange('firstName', e.target.value)}
              />
            </EditField>
            <EditField label="Nachname" id="ed-lastName">
              <Input
                type="text"
                id="ed-lastName"
                value={editData.lastName}
                onChange={(e) => handleEditChange('lastName', e.target.value)}
              />
            </EditField>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            {!isManagerView && (
              <ReadOnlyField label="E-Mail" value={localUser.email} />
            )}
            {!isManagerView && (
              <ReadOnlyField label="Personalnummer" value={localUser.personnelNumber} fallback="nicht vergeben" />
            )}
            <ReadOnlyField label="Vorname" value={localUser.firstName} />
            <ReadOnlyField label="Nachname" value={localUser.lastName} />
          </div>
        )}

        {/* Beschäftigung */}
        <SectionHeading>Beschäftigung</SectionHeading>
        {isEditing && editData ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            <EditField label="Beschäftigt seit" id="ed-empStart">
              <Input
                type="date"
                id="ed-empStart"
                value={editData.employmentStart ? editData.employmentStart.split('T')[0] : ''}
                onChange={(e) => handleEditChange('employmentStart', e.target.value)}
              />
            </EditField>
            <EditField label="Beschäftigt bis" id="ed-empEnd">
              <Input
                type="date"
                id="ed-empEnd"
                value={editData.employmentEnd ? editData.employmentEnd.split('T')[0] : ''}
                onChange={(e) => handleEditChange('employmentEnd', e.target.value)}
              />
            </EditField>
            <EditField label="Beschäftigungsart" id="ed-empType">
              <Select
                value={editData.employmentType || '_none'}
                onValueChange={(v) => handleEditChange('employmentType', (v === '_none' ? '' : v) as EmploymentType | '')}
              >
                <SelectTrigger id="ed-empType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Keine Angabe</SelectItem>
                  {EMPLOYMENT_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{EMPLOYMENT_TYPE_LABELS[type]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </EditField>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            <ReadOnlyField
              label="Beschäftigt seit"
              value={localUser.employmentStart ? new Date(localUser.employmentStart).toLocaleDateString('de-DE') : null}
            />
            <ReadOnlyField
              label="Beschäftigt bis"
              value={localUser.employmentEnd ? new Date(localUser.employmentEnd).toLocaleDateString('de-DE') : null}
              fallback="Unbefristet"
            />
            <ReadOnlyField
              label="Beschäftigungsart"
              value={localUser.employmentType ? EMPLOYMENT_TYPE_LABELS[localUser.employmentType] : null}
            />
          </div>
        )}

        {/* Vergütung — nur HR */}
        {!isManagerView && (
          <>
            <SectionHeading>Vergütung</SectionHeading>
            {isEditing && editData ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                <EditField label="Lohnart" id="ed-wageType">
                  <Select
                    value={editData.wageType || '_none'}
                    onValueChange={(v) => handleEditChange('wageType', (v === '_none' ? '' : v) as WageType | '')}
                  >
                    <SelectTrigger id="ed-wageType">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Keine Angabe</SelectItem>
                      {WAGE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{WAGE_TYPE_LABELS[type]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </EditField>
                {editData.wageType === 'FIXED' && (
                  <EditField label="Monatsgehalt (€)" id="ed-monthlySalary">
                    <Input
                      type="number"
                      id="ed-monthlySalary"
                      min="0"
                      step="0.01"
                      value={editData.monthlySalary}
                      onChange={(e) => handleEditChange('monthlySalary', e.target.value)}
                      placeholder="z.B. 2500"
                    />
                  </EditField>
                )}
                <EditField label="Vertragliche Wochenstunden" id="ed-contractualHours">
                  <Input
                    type="number"
                    id="ed-contractualHours"
                    min="0"
                    max="50"
                    step="0.5"
                    value={editData.contractualHours}
                    onChange={(e) => handleEditChange('contractualHours', e.target.value)}
                    placeholder="z.B. 20"
                  />
                </EditField>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                <ReadOnlyField
                  label="Lohnart"
                  value={localUser.wageType ? WAGE_TYPE_LABELS[localUser.wageType] : null}
                />
                {localUser.wageType === 'FIXED' && (
                  <ReadOnlyField
                    label="Monatsgehalt"
                    value={localUser.monthlySalary != null ? formatCurrency(localUser.monthlySalary) : null}
                  />
                )}
                <ReadOnlyField
                  label="Vertragliche Wochenstunden"
                  value={localUser.contractualHours != null ? `${localUser.contractualHours} h` : null}
                />
              </div>
            )}
          </>
        )}

        {/* Optionen — nur HR */}
        {!isManagerView && (
          <>
            <SectionHeading>Optionen</SectionHeading>
            {isEditing && editData ? (
              <div className="flex flex-wrap gap-6 mb-5">
                <label className="flex items-center gap-2 cursor-pointer" title="Rentenversicherungspflichtig">
                  <input
                    type="checkbox"
                    checked={editData.rv}
                    onChange={(e) => handleEditChange('rv', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-gray-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">RV-pflichtig</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" title="Hansefit-Mitgliedschaft">
                  <input
                    type="checkbox"
                    checked={editData.hansefit}
                    onChange={(e) => handleEditChange('hansefit', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-gray-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Hansefit</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer" title="Diese Person wird beim Sammel-Export nicht berücksichtigt">
                  <input
                    type="checkbox"
                    checked={editData.excludeFromExport}
                    onChange={(e) => handleEditChange('excludeFromExport', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 focus:ring-gray-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Vom Lohn-Export ausschließen</span>
                </label>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 mb-5">
                {localUser.rv && (
                  <Badge>RV-pflichtig</Badge>
                )}
                {localUser.hansefit && (
                  <Badge>Hansefit</Badge>
                )}
                {localUser.excludeFromExport && (
                  <Badge variant="warning">Vom Lohn-Export ausgeschlossen</Badge>
                )}
                {!localUser.rv && !localUser.hansefit && !localUser.excludeFromExport && (
                  <span className="text-sm italic text-gray-400 dark:text-gray-500">Keine Optionen aktiv</span>
                )}
              </div>
            )}
          </>
        )}

        {/* Betriebsdaten (bisher separater Block — jetzt hier integriert) */}
        <SectionHeading>Betriebsdaten</SectionHeading>
        {isEditing && editData ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
            <EditField label="Bundesland" id="ed-federalState">
              <Select
                value={editData.federalState}
                onValueChange={(v) => handleEditChange('federalState', v as FederalState)}
              >
                <SelectTrigger id="ed-federalState">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FEDERAL_STATE_NAMES).map(([code, name]) => (
                    <SelectItem key={code} value={code}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </EditField>
            <EditField label="Abteilung" id="ed-department">
              <Select
                value={editData.department || '_none'}
                onValueChange={(v) => handleEditChange('department', (v === '_none' ? '' : v) as Department | '')}
              >
                <SelectTrigger id="ed-department">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Keine Abteilung</SelectItem>
                  {DEPARTMENTS.map((dept) => (
                    <SelectItem key={dept} value={dept}>{DEPARTMENT_NAMES[dept]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </EditField>
            <EditField label="Jahresurlaub Vollzeit" id="ed-vacationDays">
              <div className="relative">
                <Input
                  type="number"
                  id="ed-vacationDays"
                  min={VALIDATION.vacationDays.min}
                  max={VALIDATION.vacationDays.max}
                  value={editData.vacationDaysFullTime}
                  onChange={(e) => handleSettingNumberChange('vacationDaysFullTime', e.target.value)}
                  className="pr-12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">Tage</span>
              </div>
            </EditField>
            <EditField label="Arbeitstage/Woche" id="ed-workDays">
              <div className="flex gap-4 mt-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer text-gray-900 dark:text-gray-100">
                  <input
                    type="radio"
                    name="ed-workDays"
                    value="5"
                    checked={editData.workDaysPerWeekFullTime === 5}
                    onChange={() => handleEditChange('workDaysPerWeekFullTime', 5)}
                    className="w-4 h-4 text-gray-900 dark:text-gray-100 focus:ring-gray-500"
                  />
                  <span>5 Tage</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer text-gray-900 dark:text-gray-100">
                  <input
                    type="radio"
                    name="ed-workDays"
                    value="6"
                    checked={editData.workDaysPerWeekFullTime === 6}
                    onChange={() => handleEditChange('workDaysPerWeekFullTime', 6)}
                    className="w-4 h-4 text-gray-900 dark:text-gray-100 focus:ring-gray-500"
                  />
                  <span>6 Tage</span>
                </label>
              </div>
            </EditField>
            <EditField label="Sollstunden/Tag (Vollzeit)" id="ed-hoursPerDay">
              <div className="relative">
                <Input
                  type="number"
                  id="ed-hoursPerDay"
                  min={VALIDATION.hoursPerDay.min}
                  max={VALIDATION.hoursPerDay.max}
                  step="0.5"
                  value={editData.hoursPerDayFullTime}
                  onChange={(e) => handleSettingNumberChange('hoursPerDayFullTime', e.target.value)}
                  className="pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">h</span>
              </div>
            </EditField>
            {!isManagerView && (
              <EditField label="Stundenlohn (brutto)" id="ed-hourlyWage">
                <div className="relative">
                  <Input
                    type="number"
                    id="ed-hourlyWage"
                    min={VALIDATION.hourlyWage.min}
                    max={VALIDATION.hourlyWage.max}
                    step="0.01"
                    value={editData.hourlyWage}
                    onChange={(e) => handleSettingNumberChange('hourlyWage', e.target.value)}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
                </div>
              </EditField>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
            <ReadOnlyField label="Bundesland" value={FEDERAL_STATE_NAMES[settings.federalState]} />
            <ReadOnlyField label="Abteilung" value={settings.department ? DEPARTMENT_NAMES[settings.department] : null} fallback="Keine Abteilung" />
            <ReadOnlyField label="Jahresurlaub Vollzeit" value={`${settings.vacationDaysFullTime} Tage`} />
            <ReadOnlyField label="Arbeitstage/Woche" value={`${settings.workDaysPerWeekFullTime} Tage`} />
            <ReadOnlyField label="Sollstunden/Tag (Vollzeit)" value={`${settings.hoursPerDayFullTime} h`} />
            {!isManagerView && (
              <ReadOnlyField label="Stundenlohn (brutto)" value={formatCurrency(settings.hourlyWage)} />
            )}
          </div>
        )}

        {/* Manager-Zuordnung — nur HR */}
        {!isManagerView && (
          <div className="border-t border-gray-200 dark:border-white/10 pt-4">
            <div className="flex items-center justify-between mb-3">
              <SectionHeading noMargin>
                <Users className="w-4 h-4 text-gray-500 dark:text-gray-400 inline mr-1.5" />
                Manager-Zuordnung
              </SectionHeading>
              {managersChanged && (
                <Button
                  size="sm"
                  onClick={handleSaveManagers}
                  disabled={isSavingManagers}
                >
                  {isSavingManagers ? (
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save className="w-3.5 h-3.5" />
                  )}
                  Speichern
                </Button>
              )}
            </div>

            {/* Zugeordnete Manager */}
            {selectedManagerIds.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedManagerIds.map((managerId) => {
                  const mgr = allUsers.find((u) => u.id === managerId);
                  return (
                    <span
                      key={managerId}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm
                                 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                 rounded-full border border-gray-200 dark:border-white/10"
                    >
                      {mgr ? (mgr.firstName && mgr.lastName ? `${mgr.firstName} ${mgr.lastName}` : mgr.name) : managerId}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 rounded-full"
                        onClick={() => handleRemoveManager(managerId)}
                        aria-label={`Manager ${mgr?.name || managerId} entfernen`}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Manager hinzufügen */}
            <div className="relative">
              <Input
                type="text"
                value={managerSearchTerm}
                onChange={(e) => {
                  setManagerSearchTerm(e.target.value);
                  setShowManagerDropdown(true);
                }}
                onFocus={() => setShowManagerDropdown(true)}
                placeholder="Manager hinzufügen..."
              />
              {showManagerDropdown && (
                <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-white dark:bg-gray-900
                                border border-gray-200 dark:border-white/10 rounded-lg">
                  {allUsers
                    .filter((u) =>
                      u.id !== user.id &&
                      !selectedManagerIds.includes(u.id) &&
                      (managerSearchTerm === '' ||
                        u.name.toLowerCase().includes(managerSearchTerm.toLowerCase()) ||
                        u.email.toLowerCase().includes(managerSearchTerm.toLowerCase()) ||
                        (u.firstName && u.firstName.toLowerCase().includes(managerSearchTerm.toLowerCase())) ||
                        (u.lastName && u.lastName.toLowerCase().includes(managerSearchTerm.toLowerCase())))
                    )
                    .slice(0, 10)
                    .map((u) => (
                      <Button
                        key={u.id}
                        variant="ghost"
                        onClick={() => handleAddManager(u.id)}
                        className="w-full justify-start rounded-none h-auto px-3 py-2 text-sm font-normal"
                      >
                        <span className="font-medium">{u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.name}</span>
                        <span className="ml-2 text-gray-500 dark:text-gray-400">{u.email}</span>
                      </Button>
                    ))}
                  {allUsers.filter((u) =>
                    u.id !== user.id &&
                    !selectedManagerIds.includes(u.id) &&
                    (managerSearchTerm === '' ||
                      u.name.toLowerCase().includes(managerSearchTerm.toLowerCase()) ||
                      u.email.toLowerCase().includes(managerSearchTerm.toLowerCase()))
                  ).length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                      Keine weiteren Benutzer gefunden
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Klick außerhalb schließt Dropdown */}
            {showManagerDropdown && (
              <div
                className="fixed inset-0 z-0"
                onClick={() => setShowManagerDropdown(false)}
              />
            )}
          </div>
        )}
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════════════
          Block 1b: Beschäftigungshistorie (nur HR)
          ═══════════════════════════════════════════════ */}
      {!isManagerView && (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900 dark:text-gray-50">Beschäftigungshistorie</h3>
                {historyRecords.length > 0 && (
                  <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400">
                    {historyRecords.length}
                  </span>
                )}
              </div>
            </div>
            {historyError && (
              <div className="mb-3 p-2.5 text-sm border border-red-300/60 bg-red-50 dark:border-red-500/20 dark:bg-red-900/10 rounded-lg text-red-700 dark:text-red-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {historyError}
                <button onClick={loadHistory} className="ml-auto text-xs underline hover:no-underline">Erneut versuchen</button>
              </div>
            )}
            <EmploymentTimeline
              records={historyRecords}
              onAddNew={handleAddHistory}
              onEdit={handleEditHistory}
              onDelete={(id) => setShowDeleteHistoryConfirm(id)}
              isLoading={isHistoryLoading}
            />
          </CardContent>
        </Card>
      )}

      {/* History-Änderungsdialog */}
      <EmploymentChangeDialog
        isOpen={showHistoryDialog}
        onClose={() => setShowHistoryDialog(false)}
        onSave={handleSaveHistory}
        currentValues={currentHistoryRecord}
        editRecord={historyEditRecord}
        mode={historyDialogMode}
      />

      {/* History-Löschbestätigung */}
      <Dialog open={showDeleteHistoryConfirm !== null} onOpenChange={(open) => { if (!open) setShowDeleteHistoryConfirm(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Eintrag löschen</DialogTitle>
            <DialogDescription>
              Möchten Sie diesen Historieneintrag wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteHistoryConfirm(null)}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={() => showDeleteHistoryConfirm && handleDeleteHistory(showDeleteHistoryConfirm)}
            >
              <Trash2 className="w-4 h-4" />
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════
          Block 2: Berechnete Kennzahlen (immer read-only)
          ═══════════════════════════════════════════════ */}

      {/* KPI-Karten */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={<Clock className="w-5 h-5" />}
          label="Arbeitsstunden"
          value={formatNumber(results.totalHours)}
          color="blue"
        />
        <StatCard
          icon={<Calendar className="w-5 h-5" />}
          label="Arbeitstage"
          value={results.workDays.toString()}
          color="green"
        />
        <StatCard
          icon={<Sun className="w-5 h-5" />}
          label="Urlaubsanspruch"
          value={`${results.totalVacationEntitlement} Tage`}
          color="amber"
        />
        <StatCard
          icon={<Briefcase className="w-5 h-5" />}
          label="Resturlaub"
          value={`${results.remainingVacation} Tage`}
          color={results.remainingVacation < 0 ? 'red' : 'emerald'}
        />
      </div>

      {/* Detailbereich */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Urlaubsübersicht */}
        <Card>
          <CardContent className="p-5">
          <h3 className="font-semibold text-gray-900 dark:text-gray-50 mb-4">Urlaubsübersicht</h3>
          <dl className="space-y-3">
            {results.proRataFactor != null && results.proRataFactor < 1 && (
              <>
                <DetailRow
                  label="Volle Beschäftigungsmonate"
                  value={`${results.fullEmploymentMonths} von 12`}
                />
                <DetailRow
                  label="Ganzjahresanspruch (gerundet)"
                  value={`${results.vacationEntitlementFullYear} Tage`}
                />
              </>
            )}
            <DetailRow
              label={results.proRataFactor != null && results.proRataFactor < 1 ? 'Teilurlaub §5 BUrlG (berechnet)' : 'Urlaubsanspruch (berechnet)'}
              value={`${formatNumber(results.vacationEntitlementRaw)} Tage`}
            />
            <DetailRow
              label={results.proRataFactor != null && results.proRataFactor < 1 ? 'Teilurlaub §5 BUrlG (gerundet)' : 'Urlaubsanspruch (gerundet)'}
              value={`${results.vacationEntitlementRounded} Tage`}
            />
            <DetailRow
              label="Übertrag aus Vorjahr"
              value={`${results.carryOverFromPreviousYear} Tage`}
            />
            {adjustmentSum !== 0 && (
              <DetailRow
                label="Urlaubsanpassungen"
                value={`${adjustmentSum > 0 ? '+' : ''}${adjustmentSum} Tage`}
                negative={adjustmentSum < 0}
              />
            )}
            <DetailRow
              label="Gesamtanspruch"
              value={`${results.totalVacationEntitlement} Tage`}
              highlight
            />
            {/* Genommene Urlaubstage — aufklappbar */}
            <div>
              <button
                type="button"
                className="flex justify-between items-center py-1 w-full text-left hover:bg-muted/50 rounded -mx-1 px-1 transition-colors"
                onClick={() => setShowVacationDays(!showVacationDays)}
              >
                <dt className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showVacationDays ? '' : '-rotate-90'}`} />
                  Genommene Urlaubstage
                </dt>
                <dd className="font-medium tabular-nums text-gray-700 dark:text-gray-300">
                  {results.vacationDaysTaken} Tage
                </dd>
              </button>
              {showVacationDays && (
                <div className="ml-5 mt-1 mb-1 space-y-0.5">
                  {(() => {
                    // Gleiche Logik wie vacationDaysTaken in calculations.ts
                    const vacationEntries = Object.values(entries)
                      .filter((e) => {
                        if (e.isCompanyVacation) {
                          const wt = findSegmentWageType(settingsSegments, e.date);
                          if (wt === 'FIXED') return true;
                          if (wt !== undefined) return false;
                        }
                        return e.isVacation || e.isHalfVacation;
                      })
                      .sort((a, b) => a.date.localeCompare(b.date));
                    if (vacationEntries.length === 0) {
                      return <p className="text-xs text-muted-foreground">Keine Urlaubstage gebucht.</p>;
                    }
                    return vacationEntries.map((e) => {
                      const [y, m, d] = e.date.split('-');
                      const dateObj = new Date(Number(y), Number(m) - 1, Number(d));
                      const weekday = dateObj.toLocaleDateString('de-DE', { weekday: 'short' });
                      return (
                        <div key={e.date} className="flex justify-between text-xs text-muted-foreground">
                          <span>{weekday}, {d}.{m}.{y}</span>
                          <span>{e.isHalfVacation ? '½ Tag' : '1 Tag'}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
            <DetailRow
              label="Resturlaub"
              value={`${results.remainingVacation} Tage`}
              highlight
              negative={results.remainingVacation < 0}
            />
          </dl>

          {/* Urlaubsübertrag — nur HR */}
          {!isManagerView && (
            <div className="border-t border-gray-200 dark:border-white/10 pt-4 mt-4">
              {carryOverSuccess && (
                <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
                    <Check className="w-4 h-4 flex-shrink-0" />
                    {carryOverSuccess}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    Resturlaub ins nächste Jahr übertragen
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {results.remainingVacation > 0
                      ? `${results.remainingVacation} Tage verfügbar`
                      : 'Kein Resturlaub vorhanden'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRecalculateCarryover}
                    disabled={isRecalculating}
                    title="Übertrag aus Vorjahr automatisch neu berechnen"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isRecalculating ? 'animate-spin' : ''}`} />
                    Neu berechnen
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleOpenCarryOver}
                    disabled={results.remainingVacation <= 0}
                  >
                    <ArrowRight className="w-3.5 h-3.5" />
                    Nach {year + 1} übertragen
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Urlaubsanpassungen — nur HR */}
          {!isManagerView && (
            <div className="border-t border-gray-200 dark:border-white/10 pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                  Urlaubsanpassungen {year}
                </h4>
                <div className="flex items-center gap-3">
                  {adjustmentSum !== 0 && (
                    <span className={`text-sm font-medium tabular-nums ${adjustmentSum > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {adjustmentSum > 0 ? '+' : ''}{adjustmentSum} Tage
                    </span>
                  )}
                  <Button
                    size="sm"
                    onClick={handleOpenAdjustmentDialog}
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Urlaubstage anpassen
                  </Button>
                </div>
              </div>

              {adjustmentSuccess && (
                <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
                    <Check className="w-4 h-4 flex-shrink-0" />
                    {adjustmentSuccess}
                  </div>
                </div>
              )}

              {adjustments.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs font-medium">Datum</TableHead>
                      <TableHead className="text-xs font-medium">Kategorie</TableHead>
                      <TableHead className="text-xs font-medium text-right">Tage</TableHead>
                      <TableHead className="text-xs font-medium">Begründung</TableHead>
                      <TableHead className="text-xs font-medium">Erstellt von</TableHead>
                      <TableHead className="text-xs font-medium text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adjustments.map((adj) => (
                      <TableRow key={adj.id}>
                        <TableCell className="py-2 whitespace-nowrap">
                          {new Date(adj.createdAt).toLocaleDateString('de-DE')}
                        </TableCell>
                        <TableCell className="py-2">
                          {VACATION_ADJUSTMENT_CATEGORY_LABELS[adj.category]}
                        </TableCell>
                        <TableCell className={`py-2 text-right font-medium tabular-nums whitespace-nowrap ${adj.days > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {adj.days > 0 ? '+' : ''}{adj.days}
                        </TableCell>
                        <TableCell className="py-2 max-w-[200px] truncate" title={adj.reason}>
                          {adj.reason}
                        </TableCell>
                        <TableCell className="py-2 text-muted-foreground whitespace-nowrap">
                          {adj.createdBy}
                        </TableCell>
                        <TableCell className="py-2 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => {
                              if (confirm(`Anpassung "${VACATION_ADJUSTMENT_CATEGORY_LABELS[adj.category]}: ${adj.days > 0 ? '+' : ''}${adj.days} Tage" wirklich löschen?`)) {
                                void handleDeleteAdjustment(adj.id);
                              }
                            }}
                            disabled={deletingAdjustmentId === adj.id}
                            title="Anpassung löschen"
                          >
                            {deletingAdjustmentId === adj.id ? (
                              <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {adjustments.length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Keine Urlaubsanpassungen für {year} vorhanden.
                </p>
              )}
            </div>
          )}
          </CardContent>
        </Card>

        {/* Arbeitszeit-Statistik */}
        <Card>
          <CardContent className="p-5">
          <h3 className="font-semibold text-gray-900 dark:text-gray-50 mb-4">Arbeitszeit-Statistik</h3>
          <dl className="space-y-3">
            <DetailRow
              label="Gesamtstunden"
              value={formatNumber(results.totalHours)}
            />
            <DetailRow
              label="Arbeitstage"
              value={results.workDays.toString()}
            />
            <DetailRow
              label="Ø Stunden pro Arbeitstag"
              value={formatNumber(results.averageHoursPerDay)}
            />
            <DetailRow
              label="Ø Arbeitstage pro Woche"
              value={formatNumber(results.averageWorkDaysPerWeek)}
            />
            <DetailRow
              label="Feiertage"
              value={results.holidays.toString()}
            />
            {!isManagerView && (
              <DetailRow
                label="Urlaubsentgelt pro Tag"
                value={formatCurrency(results.last13Weeks.vacationPayPerDay)}
                highlight
              />
            )}
          </dl>
          </CardContent>
        </Card>
      </div>

      {/* ═══════════════════════════════════════════════
          Block 3: Monatsübersicht (immer read-only)
          ═══════════════════════════════════════════════ */}
      <Card className="overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-white/10 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-50">Monatsübersicht {year}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Abrechnungszeitraum: 21. des Vormonats bis 20. des jeweiligen Monats
            </p>
          </div>
          {!isManagerView && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
            >
              <Download className="w-4 h-4" />
              CSV Export
            </Button>
          )}
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="text-xs font-medium uppercase tracking-wider">Monat</TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wider text-right">Stunden</TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wider text-right">Arbeitstage</TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wider text-right">Feiertage</TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wider text-right">Urlaub</TableHead>
              {!isManagerView && (
                <TableHead className="text-xs font-medium uppercase tracking-wider">
                  Urlaubsstunden
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {monthlyData.map((month) => {
              const finalization = FINALIZATION_LABELS[
                finalizationByMonth.get(month.month) ?? 'none'
              ];
              return (
                <TableRow key={month.month}>
                  <TableCell className="font-medium">
                    {month.monthName}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(month.totalHours)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {month.workDays}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {month.holidays}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {month.vacationDays}
                  </TableCell>
                  {!isManagerView && (
                    <TableCell>
                      {finalization ? (
                        <StatusBadge variant={finalization.variant} title={finalization.title}>
                          {finalization.label}
                        </StatusBadge>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-600">–</span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="font-semibold">Gesamt</TableCell>
              <TableCell className="text-right tabular-nums font-semibold">
                {formatNumber(results.totalHours)}
              </TableCell>
              <TableCell className="text-right tabular-nums font-semibold">
                {results.workDays}
              </TableCell>
              <TableCell className="text-right tabular-nums font-semibold">
                {results.holidays}
              </TableCell>
              <TableCell className="text-right tabular-nums font-semibold">
                {results.vacationDaysTaken}
              </TableCell>
              {!isManagerView && <TableCell />}
            </TableRow>
          </TableFooter>
        </Table>
      </Card>

      {/* Hinweis */}
      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
        {isManagerView
          ? 'Alle Daten sind schreibgeschützt. Lohndaten sind für Manager nicht sichtbar.'
          : 'Tageseinträge sind schreibgeschützt. Betriebsdaten können von HR bearbeitet werden.'}
      </p>

      {/* Urlaubsübertrag-Dialog */}
      <Dialog open={showCarryOverDialog} onOpenChange={(open) => { if (!open) setShowCarryOverDialog(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Urlaub übertragen</DialogTitle>
            <DialogDescription>
              Wie viele Urlaubstage von <span className="font-semibold">{displayName}</span> sollen
              nach {year + 1} übertragen werden?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="carryOverAmount">
                Anzahl Tage (max. {Math.max(0, results.remainingVacation)})
              </Label>
              <Input
                type="number"
                id="carryOverAmount"
                min={0}
                max={Math.max(0, results.remainingVacation)}
                value={carryOverAmount}
                onChange={(e) =>
                  setCarryOverAmount(
                    Math.min(
                      Math.max(0, results.remainingVacation),
                      Math.max(0, parseFloat(e.target.value) || 0)
                    )
                  )
                }
              />
            </div>

            <div className="border border-blue-300/60 bg-blue-50 dark:border-blue-500/20 dark:bg-blue-900/10 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Der übertragene Urlaub erhält ein Verfalldatum zum 31. März {year + 1}.
                  Als HR können Sie den Übertrag jederzeit anpassen.
                </p>
              </div>
            </div>

            {saveError && (
              <div className="p-3 border border-red-300/60 bg-red-50 dark:border-red-500/20 dark:bg-red-900/10 rounded-lg text-sm text-red-700 dark:text-red-300">
                {saveError}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCarryOverDialog(false)}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleCarryOver}
              disabled={carryOverAmount <= 0 || isSavingCarryOver}
            >
              {isSavingCarryOver ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {carryOverAmount} Tage übertragen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Urlaubsanpassung-Dialog */}
      <Dialog open={showAdjustmentDialog} onOpenChange={(open) => { if (!open) setShowAdjustmentDialog(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Urlaubstage anpassen</DialogTitle>
            <DialogDescription>
              Urlaubstage für {displayName} im Jahr {year} anpassen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Kategorie */}
            <div>
              <Label htmlFor="adjCategory" className="mb-1">
                Kategorie
              </Label>
              <Select
                value={adjCategory}
                onValueChange={(v) => setAdjCategory(v as VacationAdjustmentCategory)}
              >
                <SelectTrigger id="adjCategory">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(VACATION_ADJUSTMENT_CATEGORY_LABELS) as VacationAdjustmentCategory[]).map((cat) => (
                    <SelectItem key={cat} value={cat}>{VACATION_ADJUSTMENT_CATEGORY_LABELS[cat]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tage + Hinzufügen/Abziehen */}
            <div>
              <Label className="mb-1">
                Anzahl Tage
              </Label>
              <div className="flex gap-2">
                <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setAdjSign('add')}
                    className={`rounded-none rounded-l-lg ${
                      adjSign === 'add'
                        ? 'bg-green-600 text-white hover:bg-green-600 hover:text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    + Hinzufügen
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setAdjSign('subtract')}
                    className={`rounded-none rounded-r-lg border-l border-gray-300 dark:border-gray-600 ${
                      adjSign === 'subtract'
                        ? 'bg-red-600 text-white hover:bg-red-600 hover:text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    - Abziehen
                  </Button>
                </div>
                <Input
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={adjDays}
                  onChange={(e) => setAdjDays(e.target.value)}
                  placeholder="z.B. 2"
                  className="flex-1"
                />
              </div>
            </div>

            {/* Begründung */}
            <div>
              <Label htmlFor="adjReason" className="mb-1">
                Begründung <span className="text-gray-400 font-normal">(Pflichtfeld, 3-500 Zeichen)</span>
              </Label>
              <Textarea
                id="adjReason"
                value={adjReason}
                onChange={(e) => setAdjReason(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="z.B. Sonderurlaub wegen Hochzeit"
                className="resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">{adjReason.length}/500</p>
            </div>

            {/* Vorschau */}
            {adjDays && parseFloat(adjDays) > 0 && (
              <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-white/10 rounded-lg p-3">
                <p className="text-sm text-gray-700 dark:text-gray-200">
                  <span className={`font-semibold tabular-nums ${adjSign === 'add' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {parseFloat(adjDays)} Urlaubstag{parseFloat(adjDays) !== 1 ? 'e' : ''}
                  </span>
                  {' '}werden{' '}
                  <span className="font-medium">{adjSign === 'add' ? 'hinzugefügt' : 'abgezogen'}</span>
                  {' '}für{' '}
                  <span className="font-medium">{displayName}</span>
                  {' '}({year})
                </p>
              </div>
            )}

            {saveError && (
              <div className="p-3 border border-red-300/60 bg-red-50 dark:border-red-500/20 dark:bg-red-900/10 rounded-lg text-sm text-red-700 dark:text-red-300">
                {saveError}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAdjustmentDialog(false)}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSaveAdjustment}
              disabled={!adjDays || parseFloat(adjDays) <= 0 || adjReason.trim().length < 3 || isSavingAdjustment}
            >
              {isSavingAdjustment ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Hilfsfunktionen ────────────────────────────────────────

/** Zwischenüberschrift für Sektionen innerhalb Block 1 */
function SectionHeading({ children, noMargin }: { children: React.ReactNode; noMargin?: boolean }) {
  return (
    <h4 className={`text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 ${noMargin ? '' : 'mb-2'}`}>
      {children}
    </h4>
  );
}

/** Read-Only-Feldanzeige */
function ReadOnlyField({ label, value, fallback }: { label: string; value: string | null | undefined; fallback?: string }) {
  return (
    <div>
      <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
        {value || <span className="italic text-gray-400 dark:text-gray-500">{fallback || '—'}</span>}
      </dd>
    </div>
  );
}

/** Edit-Feld-Wrapper mit Label */
function EditField({ label, id, children }: { label: string; id: string; children: React.ReactNode }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs text-gray-500 dark:text-gray-400 mb-1">
        {label}
      </Label>
      {children}
    </div>
  );
}

/** Badge für Optionen im Read-Only-Modus */
function Badge({ children, variant = 'default' }: { children: React.ReactNode; variant?: 'default' | 'warning' }) {
  const classes = variant === 'warning'
    ? 'border-amber-300/60 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-900/10 text-amber-700 dark:text-amber-300'
    : 'bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-white/10';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full border ${classes}`}>
      {children}
    </span>
  );
}

// ─── Bestehende Hilfsfunktionen ─────────────────────────────

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: 'blue' | 'green' | 'amber' | 'emerald' | 'red';
}

function StatCard({ icon, label, value, color }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    amber: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
    red: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colorClasses[color]}`}>
          {icon}
        </div>
        <p className="text-2xl font-bold tabular-nums text-gray-900 dark:text-gray-100">{value}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      </CardContent>
    </Card>
  );
}

interface DetailRowProps {
  label: string;
  value: string;
  highlight?: boolean;
  negative?: boolean;
}

function DetailRow({ label, value, highlight, negative }: DetailRowProps) {
  return (
    <div className={`flex justify-between items-center py-1 ${highlight ? 'border-t border-gray-200 dark:border-white/10 pt-2 mt-2' : ''}`}>
      <dt className="text-gray-600 dark:text-gray-400">{label}</dt>
      <dd className={`font-medium tabular-nums ${negative ? 'text-red-600 dark:text-red-400' : highlight ? 'text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>
        {value}
      </dd>
    </div>
  );
}
